import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { PanelLeftIcon, PanelRightIcon, History, MessageSquare } from 'lucide-react';

interface ChatLayoutProps {
  children: React.ReactNode;
  sidebar: React.ReactNode;
}

export function ChatLayout({ children, sidebar }: ChatLayoutProps) {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  
  // When mobile state changes, update sidebar visibility
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  return (
    <div className="flex h-full relative">
      {/* Sidebar */}
      <div className={cn(
        "h-full border-r border-indigo-500/10 bg-[#0a0e18] transition-all duration-300 overflow-hidden",
        sidebarOpen 
          ? "w-56 md:w-[220px]" 
          : "w-0 md:w-0"
      )}>
        {sidebar}
      </div>
      
      {/* Toggle button with improved clarity */}
      <Button 
        variant="outline" 
        size="sm"
        className="absolute top-3 left-3 z-50 px-2.5 py-1.5 bg-[#162038]/90 hover:bg-[#1e2a4a] text-white border border-indigo-500/30 shadow-sm flex items-center gap-1.5 backdrop-blur-sm"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        title={sidebarOpen ? "Hide chat history" : "Show chat history"}
      >
        {sidebarOpen ? <PanelLeftIcon className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}
        <span className="text-xs font-medium">{sidebarOpen ? "Hide" : "History"}</span>
      </Button>
      
      {/* Main content */}
      <div className="flex-1 h-full overflow-hidden">
        {children}
      </div>
    </div>
  );
}
